<template>
  <div class="activity-publish">
    <!-- 顶部导航栏 -->
    <AppHeader />
    
    <!-- Banner区域 -->
    <div class="banner">
      <div class="banner-background">
        <div class="city-skyline">
          <img src="@/images/index2.png" alt="株洲港口" class="skyline-image" />
        </div>
      </div>
    </div>

    <!-- 主要内容区域 -->
    <div class="main-content">
      <div class="content-wrapper">
        <!-- 侧边栏 -->
        <div class="sidebar">
          <div class="sidebar-title">活动分类</div>
          <div class="sidebar-menu">
            <div 
              class="menu-item" 
              :class="{ active: activeCategory === 'preview' }"
              @click="setActiveCategory('preview')"
            >
              <i class="icon-preview"></i>
              活动预告
            </div>
            <div 
              class="menu-item" 
              :class="{ active: activeCategory === 'review' }"
              @click="setActiveCategory('review')"
            >
              <i class="icon-review"></i>
              活动回顾
            </div>
          </div>
        </div>

        <!-- 右侧内容区域 -->
        <div class="content-area">
          <!-- 搜索栏 -->
          <div class="search-section">
            <div class="search-bar">
              <el-input
                v-model="searchKeyword"
                placeholder="请输入关键词搜索活动"
                class="search-input"
                @keyup.enter="searchActivities"
              >
                <el-button slot="append" icon="el-icon-search" @click="searchActivities"></el-button>
              </el-input>
            </div>
          </div>

          <!-- 活动列表 -->
          <div class="activity-list" v-loading="loading">
            <div v-if="filteredActivities.length === 0 && !loading" class="empty-state">
              <p>暂无{{ activeCategory === 'preview' ? '活动预告' : '活动回顾' }}数据</p>
            </div>
            <template v-else>
              <div class="activity-item" v-for="(activity, index) in filteredActivities" :key="activity.id" :class="{ 'featured': index === 99999 && currentPage === 1 }">
              <div class="activity-date">{{ activity.date }}</div>
              <div class="activity-content">
                <h3 class="activity-title" @click="viewActivityDetail(activity)">{{ activity.title }}</h3>
                <p class="activity-summary" v-html="activity.summary"></p>
                <div class="activity-info">
                  <span class="activity-location">{{ activity.location }}</span>
                  <span class="activity-contact">联系人：{{ activity.contact }} {{ activity.mobile }}</span>
                </div>
                <div class="activity-tags">
                  <span class="tag" v-for="tag in activity.tags" :key="tag">{{ tag }}</span>
                </div>
              </div>
              </div>
            </template>
          </div>

          <!-- 分页 -->
          <div class="pagination-section">
            <el-pagination
              @current-change="handleCurrentChange"
              :current-page="currentPage"
              :page-size="pageSize"
              layout="prev, pager, next"
              :total="totalActivities"
            >
            </el-pagination>
          </div>
        </div>
      </div>
    </div>

    <!-- 底部 -->
    <AppFooter />
  </div>
</template>

<script>
import AppHeader from './AppHeader.vue'
import AppFooter from './AppFooter.vue'
import { api } from '@/utils/api'

export default {
  name: 'ActivityPublish',
  components: {
    AppHeader,
    AppFooter
  },
  data() {
    return {
      activeCategory: 'preview',
      searchKeyword: '',
      currentPage: 1,
      pageSize: 8,
      loading: false,
      activities: {
        preview: [],
        review: []
      }
    }
  },
  async mounted() {
    await api.checkAndGetToken()
    
    // 检查路由查询参数，如果有category参数则设置对应的分类
    const category = this.$route.query.category
    if (category === 'review') {
      this.activeCategory = 'review'
      await this.loadActivityData('review')
    } else {
      // 页面初始化时加载默认分类（活动预告）的数据
      await this.loadActivityData('preview')
    }
  },
  computed: {
    filteredActivities() {
      let filtered = this.activities[this.activeCategory] || []
      
      // 按关键词搜索
      if (this.searchKeyword) {
        filtered = filtered.filter(activity => 
          activity.title.includes(this.searchKeyword) || 
          activity.summary.includes(this.searchKeyword)
        )
      }
      
      // 分页
      const start = (this.currentPage - 1) * this.pageSize
      const end = start + this.pageSize
      return filtered.slice(start, end)
    },
    totalActivities() {
      let filtered = this.activities[this.activeCategory] || []
      
      if (this.searchKeyword) {
        filtered = filtered.filter(activity => 
          activity.title.includes(this.searchKeyword) || 
          activity.summary.includes(this.searchKeyword)
        )
      }
      
      return filtered.length
    }
  },
  methods: {
    async loadActivityData(category = null) {
      try {
        this.loading = true
        
        // 如果指定了分类，只加载该分类的数据
        if (category) {
          if (category === 'preview') {
            // 调用活动预告接口 (zero)
            const previewResponse = await api.getActivityZero()
            if (previewResponse && previewResponse.success && previewResponse.result) {
              this.activities.preview = this.transformActivityData(previewResponse.result.records, 'preview')
            }
          } else if (category === 'review') {
            // 调用活动回顾接口 (one)
            const reviewResponse = await api.getActivityOne()
            if (reviewResponse && reviewResponse.success && reviewResponse.result) {
              this.activities.review = this.transformActivityData(reviewResponse.result.records, 'review')
            }
          }
        } else {
          // 如果没有指定分类，加载当前激活分类的数据
          await this.loadActivityData(this.activeCategory)
        }
      } catch (error) {
        console.error('加载活动数据失败:', error)
        this.$message.error('加载活动数据失败，请稍后重试')
      } finally {
        this.loading = false
      }
    },
    
    transformActivityData(data, category) {
      // 根据API返回的数据结构转换为组件需要的格式
      if (Array.isArray(data)) {
        return data.map((item, index) => ({
          id: item.id || index + 1,
          date: item.date || item.createTime || new Date().toLocaleDateString(),
          title: item.qyTitle || '活动标题',
          summary: item.summary || item.description || item.content || '活动描述',
          location: item.qyAddress || '待定',
          contact: item.qyPerson || '联系人待定',
          mobile: item.qyPhone,
          tags: item.tags || ['活动'],
          category: category
        }))
      }
      return []
    },
    
    async setActiveCategory(category) {
      this.activeCategory = category
      this.currentPage = 1
      // 切换分类时加载对应分类的数据
      await this.loadActivityData(category)
    },
    searchActivities() {
      this.currentPage = 1
    },
    handleCurrentChange(page) {
      this.currentPage = page
    },
    viewActivityDetail(activity) {
      console.log('查看活动详情:', activity)
      this.$message.info('活动详情功能开发中...')
    }
  }
}
</script>

<style scoped>
.activity-publish {
  width: 100%;
  font-family: 'Microsoft YaHei', Arial, sans-serif;
  color: #333;
  background-color: white;
  padding-top: 0;
}

/* Banner区域样式 */
.banner {
  position: relative;
  height: 400px;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
}

.banner-background {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 1;
}

.city-skyline {
  left: 0;
  width: 100%;
  height: 100%;
}

.skyline-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

/* 主要内容区域 */
.main-content {
  background-color: #f5f5f5;
  min-height: calc(100vh - 300px);
  padding: 40px 0;
}

.content-wrapper {
  max-width: 1200px;
  margin: 0 auto;
  display: flex;
  gap: 30px;
  padding: 0 20px;
}

/* 侧边栏样式 */
.sidebar {
  width: 250px;
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  height: fit-content;
  overflow: hidden;
}

.sidebar-title {
  background: linear-gradient(135deg, #4A90E2 0%, #357ABD 100%);
  color: white;
  padding: 20px;
  font-size: 18px;
  font-weight: 600;
  text-align: center;
}

.sidebar-menu {
  padding: 0;
}

.menu-item {
  padding: 15px 20px;
  cursor: pointer;
  transition: all 0.3s ease;
  border-bottom: 1px solid #f0f0f0;
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 15px;
}

.menu-item:hover {
  background-color: #f8f9fa;
  color: #4A90E2;
}

.menu-item.active {
  background-color: #e8f4fd;
  color: #4A90E2;
  border-left: 4px solid #4A90E2;
  font-weight: 600;
}

.icon-preview::before {
  content: "📅";
  margin-right: 5px;
}

.icon-review::before {
  content: "📋";
  margin-right: 5px;
}

/* 右侧内容区域 */
.content-area {
  flex: 1;
}

/* 搜索栏 */
.search-section {
  margin-bottom: 30px;
  display: flex;
  justify-content: flex-end;
}

.search-bar {
  max-width: 400px;
  width: 100%;
}

.search-input {
  width: 100%;
}

.search-input .el-input__inner {
  border-radius: 20px;
  border: 1px solid #ddd;
  padding-left: 20px;
}

.search-input .el-input-group__append {
  border-radius: 0 20px 20px 0;
  border-left: none;
  background: #4A90E2;
}

.search-input .el-input-group__append .el-button {
  background: #4A90E2;
  border: none;
  color: white;
}

.search-input .el-input-group__append .el-button:hover {
  background: #357ABD;
}

/* 活动列表 */
.activity-list {
  margin-bottom: 30px;
}

.activity-item {
  background: white;
  border-radius: 8px;
  padding: 20px;
  margin-bottom: 20px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  border-left: 4px solid #4A90E2;
  transition: all 0.3s ease;
}

.activity-item:hover {
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15);
  transform: translateY(-2px);
}

/* 特色活动项样式 */
.activity-item.featured {
  background: linear-gradient(135deg, #4A90E2 0%, #357ABD 100%);
  color: white;
  padding: 30px;
  border-radius: 8px;
  border-left: none;
  margin-bottom: 20px;
  box-shadow: 0 4px 20px rgba(74, 144, 226, 0.3);
}

.activity-item.featured .activity-date {
  background: rgba(255, 255, 255, 0.2);
  color: white;
  border: 1px solid rgba(255, 255, 255, 0.3);
}

.activity-item.featured .activity-title {
  color: white;
  font-size: 20px;
  font-weight: 700;
}

.activity-item.featured .activity-title:hover {
  color: #ecf0f1;
}

.activity-item.featured .activity-summary {
  color: rgba(255, 255, 255, 0.9);
  font-size: 15px;
}

.activity-item.featured .activity-info {
  color: rgba(255, 255, 255, 0.9);
}

.activity-item.featured .tag {
  background: rgba(255, 255, 255, 0.2);
  color: white;
  border: 1px solid rgba(255, 255, 255, 0.3);
}

.activity-date {
  display: inline-block;
  font-size: 12px;
  color: white;
  font-weight: 500;
  background: #4A90E2;
  padding: 4px 12px;
  border-radius: 4px;
  margin-bottom: 12px;
}

.activity-content {
  width: 100%;
}

.activity-title {
  font-size: 18px;
  font-weight: 600;
  color: #333;
  margin: 0 0 10px 0;
  cursor: pointer;
  transition: color 0.3s ease;
  line-height: 1.4;
}

.activity-title:hover {
  color: #4A90E2;
}

.activity-summary {
  font-size: 14px;
  color: #666;
  line-height: 1.6;
  margin: 0 0 15px 0;
}

/* 富文本内容样式 */
.activity-summary p {
  margin: 0 0 8px 0;
}

.activity-summary strong {
  font-weight: bold;
  color: #333;
}

.activity-summary em {
  font-style: italic;
}

.activity-summary ul,
.activity-summary ol {
  margin: 8px 0;
  padding-left: 20px;
}

.activity-summary li {
  margin-bottom: 4px;
}

.activity-summary a {
  color: #4A90E2;
  text-decoration: none;
}

.activity-summary a:hover {
  text-decoration: underline;
}

.activity-summary br {
  line-height: 1.6;
}

.activity-info {
  display: flex;
  gap: 20px;
  margin-bottom: 15px;
  font-size: 13px;
  color: #888;
}

.activity-location::before {
  content: "📍 ";
}

.activity-contact::before {
  content: "📞 ";
}

.activity-tags {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.tag {
  background: #e8f4fd;
  color: #4A90E2;
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 12px;
  border: 1px solid #d1e9ff;
}

/* 分页 */
.pagination-section {
  display: flex;
  justify-content: center;
  margin-top: 30px;
}

/* 响应式设计 */
@media (max-width: 768px) {
  .content-wrapper {
    flex-direction: column;
    gap: 20px;
    padding: 0 10px;
  }
  
  .sidebar {
    width: 100%;
  }
  
  .sidebar-menu {
    display: flex;
    overflow-x: auto;
  }
  
  .menu-item {
    white-space: nowrap;
    min-width: 120px;
    justify-content: center;
  }
  
  .search-section {
    justify-content: center;
  }
  
  .search-bar {
    max-width: 100%;
  }
  
  .activity-item {
    padding: 15px;
  }
  
  .activity-item.featured {
    padding: 20px;
  }
  
  .activity-info {
    flex-direction: column;
    gap: 5px;
  }
}

.empty-state {
  text-align: center;
  padding: 60px 20px;
  color: #999;
  font-size: 16px;
}
</style>